import { NamedSchemaError, SchemaFailureInfo } from "@reduxjs/toolkit/query";
import { reportSchemaFailure } from "_shared/_errors/client-error-reporter";

export function logSchemaFailure(error: NamedSchemaError, info: SchemaFailureInfo) {
  reportSchemaFailure({
    schemaName: error.schemaName,
    operation: info.endpoint,
    phase: info.type,
    fields: error.issues.map((issue) => issue.path?.map(String).join(".") ?? "unknown"),
  });
}

export type SchemaFallbackInfo = {
  /** The schema that met the value, e.g. `"NotificationSchema"`. */
  schemaName: string;
  /** The field whose value was not recognized, e.g. `"actionRequired"`. */
  field: string;
  /** The value the schema could not recognize. */
  value: unknown;
  /** What the schema substituted so the rest of the payload survives. */
  fallback: unknown;
};

const reportedFallbacks = new Set<string>();

/**
 * The counterpart to {@link logSchemaFailure} for a value a schema deliberately degrades instead of
 * rejecting — a closed enum widened with `.catch(...)` so one unrecognized field cannot discard the
 * whole payload.
 *
 * The frontend ships to CloudFront and the API to ECS, so a deploy is never atomic and users hold
 * cached bundles besides. A bundle therefore meets values the API has already started emitting. That
 * is expected and survivable, but it must not be silent: an unrecognized value means this bundle is
 * behind the API, and the console is the only place that shows it.
 *
 * Reports each distinct schema and field once without including the unrecognized value or fallback.
 */
export function logSchemaFallback(info: SchemaFallbackInfo) {
  const { schemaName, field } = info;
  const key = `${schemaName}.${field}`;
  if (reportedFallbacks.has(key)) return;
  reportedFallbacks.add(key);

  reportSchemaFailure({
    schemaName,
    operation: "schema-fallback",
    phase: "decode",
    fields: [field],
  });
}

/** Clears the report-once memory. Exported for tests that assert on {@link logSchemaFallback}. */
export function resetSchemaFallbackLog() {
  reportedFallbacks.clear();
}
